import { useEffect, useRef, useState } from 'react';
import styles from './IconDropdown.module.css';

export interface IconDropdownOption {
  key: string;
  label: string;
  icon?: string;
}

interface IconDropdownProps {
  triggerIcon: string;
  triggerTitle: string;
  options: IconDropdownOption[];
  activeKey: string;
  onSelect: (key: string) => void;
}

// Shared trigger+dropdown+click-outside-to-close atom for small icon-button
// menus in the navbar (theme picker, language switcher, ...) so each one
// doesn't hand-roll its own copy of this same open/close behavior and CSS.
export const IconDropdown = ({ triggerIcon, triggerTitle, options, activeKey, onSelect }: IconDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.container} ref={dropdownRef}>
      <div className={styles.trigger} onClick={() => setIsOpen(!isOpen)} title={triggerTitle}>
        <span className="material-icons">{triggerIcon}</span>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          {options.map((option) => (
            <div
              key={option.key}
              className={`${styles.option} ${activeKey === option.key ? styles.active : ''}`}
              onClick={() => {
                onSelect(option.key);
                setIsOpen(false);
              }}
            >
              {option.icon && <span className="material-icons">{option.icon}</span>}
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
